import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import BackToDashboard from "@/components/BackToDashboard";
import api from "@/lib/api";
import {
  Users,
  UserPlus,
  Crown,
  Shield,
  Mail,
  X,
  Check,
  Sparkles,
  Trash2,
  Lock,
  ArrowUpRight,
  ShieldCheck,
  Loader2,
  Calendar,
  Percent,
} from "lucide-react";

/**
 * Team.jsx
 * ------------------------------------------------------------------
 * Pro-only page for inviting team members. Wired to:
 *   GET    /team/members        -> { owner, included_seats, members[] }
 *   POST   /team/invite         -> { name, email }
 *   DELETE /team/members/:id
 *   POST   /team/seats/order    -> { order_id, amount, currency, key_id }
 *   POST   /team/seats/verify   -> { razorpay_order_id, razorpay_payment_id, razorpay_signature }
 * Self-fetches plan/country via GET /me, same as Plans.jsx does, so this
 * component needs no props from TeamPage.jsx.
 * ------------------------------------------------------------------
 */

const ROLES = {
  admin: { label: "Admin", icon: Crown, tone: "bg-hotpink text-white" },
  member: { label: "Member", icon: Shield, tone: "bg-lilac text-ink" },
};

const STATUS_LABEL = {
  pending: { label: "Pending", tone: "bg-peach text-ink" },
  active: { label: "Active", tone: "bg-mint text-ink" },
  suspended: { label: "Suspended", tone: "bg-ink/10 text-ink/60" },
};

const AVATAR_COLORS = [
  "from-emerald-400 to-green-500",
  "from-sky-400 to-indigo-500",
  "from-amber-400 to-orange-500",
  "from-fuchsia-400 to-purple-500",
  "from-rose-400 to-red-500",
];

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function initials(nameOrEmail) {
  const base = (nameOrEmail || "").trim();
  if (!base) return "?";
  const parts = base.split(/\s+/);
  if (parts.length > 1) return parts.slice(0, 2).map((s) => s[0]).join("").toUpperCase();
  return base[0].toUpperCase();
}

// seatOrder.amount is in the smallest currency unit (paise / cents).
function formatSeatAmount(amountSmallestUnit, symbol) {
  const value = (amountSmallestUnit || 0) / 100;
  const formatted = Number.isInteger(value) ? value.toString() : value.toFixed(2);
  return `${symbol}${formatted}`;
}

function formatSeatExpiry(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
}

export default function Team() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState(null); // { owner, included_seats, members }
  const [addOpen, setAddOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [seatBusy, setSeatBusy] = useState(false);
  const [seatOrderLoading, setSeatOrderLoading] = useState(false);
  const [seatOrder, setSeatOrder] = useState(null); // { order_id, amount, currency, key_id, expires_at, is_prorated, remaining_days }
  const [seatConfirmOpen, setSeatConfirmOpen] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const loadTeam = () =>
    api
      .get("/team/members")
      .then(({ data }) => setTeam(data))
      .catch(() => setTeam(null));

  useEffect(() => {
    Promise.all([api.get("/me"), api.get("/team/members").catch(() => null)])
      .then(([meResp, teamResp]) => {
        setMe(meResp.data);
        if (teamResp) setTeam(teamResp.data);
      })
      .catch(() => toast.error("Couldn't load your account. Please try again."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <section className="grid min-h-screen place-items-center bg-cream">
        <Loader2 className="h-6 w-6 animate-spin text-ink/40" />
      </section>
    );
  }

  if (me?.plan !== "pro") {
    return <ProGate navigate={navigate} />;
  }

  const currencySymbol = (me?.country || "IN") === "IN" ? "₹" : "$";
  const extraSeatPrice = (me?.country || "IN") === "IN" ? 499 : 5;

  const members = team?.members || [];
  const includedSeats = team?.included_seats ?? 2;
  const seatsUsed = members.filter((m) => m.status !== "suspended").length;
  const seatsFull = seatsUsed >= includedSeats;

  const invite = async ({ name, email }) => {
    setInviteBusy(true);
    try {
      await api.post("/team/invite", { name, email });
      toast.success("Invite sent — they'll receive an email shortly.");
      setAddOpen(false);
      await loadTeam();
    } catch (e) {
      const err = e?.response?.data?.error;
      if (err === "seats_full") {
        toast.error("All seats are taken. Add another seat first.");
        setAddOpen(false);
        setUpgradeOpen(true);
      } else if (err === "pro_plan_required") {
        toast.error("Team requires the Pro plan.");
      } else {
        toast.error("Couldn't send the invite. Try again.");
      }
    } finally {
      setInviteBusy(false);
    }
  };

  const removeMember = async (id) => {
    setRemovingId(id);
    try {
      await api.post("/team/members/remove", { id });
      toast.success("Team member removed.");
      await loadTeam();
    } catch {
      toast.error("Couldn't remove that member. Try again.");
    } finally {
      setRemovingId(null);
    }
  };
  
  const onAddClick = () => {
    if (seatsFull) {
      setUpgradeOpen(true);
    } else {
      setAddOpen(true);
    }
  };

  // Step 1: ask the backend what this seat will cost and when it (and any
  // existing seats) will expire — server computes proration, we just show it.
  const fetchSeatOrder = async () => {
    setSeatOrderLoading(true);
    try {
      const { data } = await api.post("/team/seats/order");
      setSeatOrder(data);
      setUpgradeOpen(false);
      setSeatConfirmOpen(true);
    } catch {
      toast.error("Couldn't calculate seat pricing. Try again.");
    } finally {
      setSeatOrderLoading(false);
    }
  };

  // Step 2: merchant has seen the price + expiry date and confirmed — now
  // actually open Razorpay checkout against the order already created above.
  const payForSeat = async () => {
    if (!seatOrder) return;
    setSeatBusy(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Razorpay failed to load");

      const options = {
        key: seatOrder.key_id,
        amount: seatOrder.amount,
        currency: seatOrder.currency,
        name: "Checkout DM",
        description: "Extra team seat",
        order_id: seatOrder.order_id,
        theme: { color: "#FF3366" },
        handler: async (resp) => {
          try {
            await api.post("/team/seats/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Seat added.");
            setSeatConfirmOpen(false);
            setSeatOrder(null);
            setAddOpen(true);
            await loadTeam();
          } catch {
            toast.error("Payment went through but we couldn't confirm it here. Refresh in a minute — if the seat hasn't appeared, contact support.");
          } finally {
            setSeatBusy(false);
          }
        },
        modal: { ondismiss: () => setSeatBusy(false) },
      };
      const rp = new window.Razorpay(options);
      rp.on("payment.failed", () => {
        toast.error("Payment failed. Please try again.");
        setSeatBusy(false);
      });
      rp.open();
    } catch {
      toast.error("Couldn't start checkout. Try again.");
      setSeatBusy(false);
    }
  };

  return (
    <section className="min-h-screen bg-cream px-6 py-12 md:px-14 md:py-16">
      <div className="mx-auto max-w-5xl">
        <BackToDashboard />
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-hotpink">Team</p>
        <h1 className="mt-3 text-4xl font-black tracking-tight text-ink md:text-6xl">
          Bring your people in.
        </h1>
        <p className="mt-4 max-w-xl text-ink/60">
          Add up to {includedSeats} teammates on Pro. Need more hands? Buy extra seats — pay per
          seat, cancel anytime.
        </p>
        <p className="mt-2">
          <Link to="/team-guide" className="text-sm font-semibold text-hotpink hover:underline">
            How team access works — invites, logins & permissions →
          </Link>
        </p>
        <p className="mt-1.5 text-xs text-ink/50">
          The short version for your team: they log in anytime at{" "}
          <span className="font-semibold text-ink">checkoutdm.com/team/login</span> — no password
          needed.
        </p>

        {/* Seat summary */}
        <div className="mt-10 grid gap-4 md:grid-cols-3">
          <SummaryTile
            label="Included seats"
            value={`${seatsUsed} / ${includedSeats}`}
            tone="bg-mint"
            icon={Users}
          />
          <SummaryTile label="Owner" value="1 admin" tone="bg-lilac" icon={Crown} />
          <SummaryTile
            label="Extra seat price"
            value={`${currencySymbol}${extraSeatPrice}/mo`}
            tone="bg-peach"
            icon={Sparkles}
          />
        </div>

        {/* Members list */}
        <div className="mt-10 overflow-hidden rounded-3xl border border-ink/10 bg-white">
          <div className="flex flex-col gap-3 border-b border-ink/10 px-6 py-5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div>
              <h2 className="text-lg font-bold text-ink">Members</h2>
              <p className="text-xs text-ink/50">
                Owners can invite and remove members. Members can view & fulfil orders.
              </p>
            </div>
            <button
              onClick={onAddClick}
              data-testid="team-add-btn"
              className={`inline-flex w-fit items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold ${
                seatsFull
                  ? "bg-gradient-to-r from-hotpink via-red-500 to-orange-500 text-white"
                  : "bg-ink text-cream hover:bg-ink/85"
              }`}
            >
              {seatsFull ? (
                <>
                  <Sparkles className="h-4 w-4" /> Add extra seat
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4" /> Invite member
                </>
              )}
            </button>
          </div>

          <ul className="divide-y divide-ink/5">
            <li className="flex flex-col gap-3 px-6 py-4 sm:flex-row sm:items-center sm:justify-between" data-testid="member-owner">
              <div className="flex min-w-0 items-center gap-4">
                <div className="grid h-11 w-11 flex-none place-items-center rounded-full bg-gradient-to-br from-hotpink via-red-500 to-orange-500 text-sm font-bold text-white">
                  {initials(team?.owner?.name || me?.username)}
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-ink">
                    {team?.owner?.name || me?.username || "You"} (Owner)
                  </p>
                  <p className="text-xs text-ink/50">Connected Instagram account</p>
                </div>
              </div>
              <span className="inline-flex w-fit items-center gap-1 rounded-full bg-hotpink px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-white">
                <Crown className="h-3 w-3" /> Admin
              </span>
            </li>

            {members.map((m, i) => (
              <MemberRow
                key={m.id}
                member={m}
                avatarColor={AVATAR_COLORS[i % AVATAR_COLORS.length]}
                removing={removingId === m.id}
                onRemove={() => removeMember(m.id)}
              />
            ))}

            {Array.from({ length: Math.max(0, includedSeats - members.length) }).map((_, i) => (
              <li key={`empty-${i}`} className="flex flex-col gap-3 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-center gap-4">
                  <div className="grid h-11 w-11 flex-none place-items-center rounded-full border-2 border-dashed border-ink/20 text-ink/30">
                    <UserPlus className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-ink/40">Empty seat</p>
                    <p className="text-xs text-ink/40">Invite a teammate to fill this seat</p>
                  </div>
                </div>
                <button
                  onClick={() => setAddOpen(true)}
                  data-testid={`invite-empty-${i}`}
                  className="w-fit rounded-full border border-ink/15 bg-white px-3 py-1.5 text-xs font-semibold text-ink hover:bg-ink/5"
                >
                  Invite
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <AnimatePresence>
        {addOpen && (
          <InviteDialog onClose={() => setAddOpen(false)} onSubmit={invite} busy={inviteBusy} />
        )}
        {upgradeOpen && (
          <UpgradeSeatDialog
            onCancel={() => setUpgradeOpen(false)}
            onConfirm={fetchSeatOrder}
            price={extraSeatPrice}
            symbol={currencySymbol}
            busy={seatOrderLoading}
          />
        )}
        {seatConfirmOpen && seatOrder && (
          <ConfirmSeatDialog
            order={seatOrder}
            symbol={currencySymbol}
            onCancel={() => { setSeatConfirmOpen(false); setSeatOrder(null); }}
            onConfirm={payForSeat}
            busy={seatBusy}
          />
        )}
      </AnimatePresence>
    </section>
  );
}

// -------------------- Row --------------------------------------------------
function MemberRow({ member, avatarColor, onRemove, removing }) {
  const roleMeta = ROLES.member;
  const RoleIcon = roleMeta.icon;
  const statusMeta = STATUS_LABEL[member.status] || STATUS_LABEL.pending;
  const displayName = member.name || member.email;
  return (
    <li className="flex flex-col gap-3 px-6 py-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`member-${member.id}`}>
      <div className="flex min-w-0 items-center gap-4">
        <div
          className={`grid h-11 w-11 flex-none place-items-center rounded-full bg-gradient-to-br ${avatarColor} text-sm font-bold text-white`}
        >
          {initials(displayName)}
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-sm font-semibold text-ink">{displayName}</p>
            <span className={`w-fit rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${statusMeta.tone}`}>
              {statusMeta.label}
            </span>
          </div>
          <p className="truncate text-xs text-ink/50">{member.email}</p>
        </div>
      </div>

      <div className="flex flex-none items-center gap-3">
        <span
          className={`inline-flex w-fit items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider ${roleMeta.tone}`}
        >
          <RoleIcon className="h-3 w-3" />
          {roleMeta.label}
        </span>
        <button
          onClick={onRemove}
          disabled={removing}
          data-testid={`remove-${member.id}`}
          className="grid h-8 w-8 flex-none place-items-center rounded-full border border-ink/15 bg-white text-ink/60 hover:border-hotpink hover:text-hotpink disabled:opacity-50"
          title="Remove"
        >
          {removing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
        </button>
      </div>
    </li>
  );
}

// -------------------- Summary tile ------------------------------------------
function SummaryTile({ label, value, tone, icon: Icon }) {
  return (
    <div className={`relative overflow-hidden rounded-3xl ${tone} p-5`}>
      <div className="flex items-start justify-between">
        <p className="text-[11px] font-bold uppercase tracking-widest text-ink/70">{label}</p>
        <div className="grid h-8 w-8 place-items-center rounded-full bg-white/80 text-ink">
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <p className="mt-8 text-3xl font-black tracking-tight text-ink">{value}</p>
    </div>
  );
}

// -------------------- Invite modal ------------------------------------------
function InviteDialog({ onClose, onSubmit, busy }) {
  const [form, setForm] = useState({ name: "", email: "" });
  const [err, setErr] = useState({});

  const submit = (e) => {
    e.preventDefault();
    const next = {};
    if (!form.name.trim()) next.name = "Required.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) next.email = "Enter a valid email.";
    setErr(next);
    if (Object.keys(next).length === 0) onSubmit(form);
  };

  return (
    <ModalShell onClose={onClose} testId="invite-dialog">
      <form onSubmit={submit}>
        <div className="flex items-center justify-between border-b border-ink/10 px-6 py-4">
          <h3 className="text-lg font-bold text-ink">Invite a teammate</h3>
          <button
            type="button"
            onClick={onClose}
            className="grid h-9 w-9 place-items-center rounded-full bg-white hover:bg-ink/5"
          >
            <X className="h-4 w-4 text-ink" />
          </button>
        </div>

        <div className="space-y-4 p-6">
          <ModalField label="Full name" required error={err.name}>
            <input
              autoFocus
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Riya Kapoor"
              className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
              data-testid="invite-name"
            />
          </ModalField>

          <ModalField label="Email" required error={err.email}>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="riya@yourbrand.com"
                className="w-full rounded-2xl border border-ink/15 bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-hotpink"
                data-testid="invite-email"
              />
            </div>
          </ModalField>

          <p className="text-xs text-ink/50">
            Every invite joins as a <span className="font-semibold text-ink">Member</span> —
            CheckoutDM keeps one Admin per business, so members can't be promoted or remove the
            owner.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-ink/10 bg-white px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-ink/15 bg-white px-4 py-2 text-sm font-semibold text-ink hover:bg-ink/5"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="invite-submit"
            className="inline-flex items-center gap-1.5 rounded-full bg-ink px-5 py-2 text-sm font-semibold text-cream hover:bg-ink/85 disabled:opacity-70"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Send invite
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// -------------------- Upgrade seat modal ------------------------------------
function UpgradeSeatDialog({ onCancel, onConfirm, price, symbol, busy }) {
  return (
    <ModalShell onClose={onCancel} testId="upgrade-seat-dialog">
      <div className="p-8">
        <div className="grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-hotpink via-red-500 to-orange-500 text-white">
          <Sparkles className="h-6 w-6" />
        </div>
        <h3 className="mt-5 text-2xl font-black tracking-tight text-ink">
          Add another seat to your Pro plan
        </h3>
        <p className="mt-2 text-sm text-ink/60">
          Every extra seat lets one more teammate log in, fulfil orders and reply to DMs.
        </p>

        <div className="mt-6 rounded-2xl border border-ink/10 bg-ink/[0.02] p-5">
          <div className="flex items-baseline justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-ink/50">
                Additional seat
              </p>
              <span className="mt-1 block text-[10px] font-bold uppercase tracking-wide text-ink/40">Up to</span>
              <p className="text-2xl font-black text-ink">
                {symbol}
                {price}
                <span className="ml-1 text-sm font-semibold text-ink/50">/ month</span>
              </p>
              <p className="mt-1 text-xs text-ink/50">
                If you already have an active extra seat, this one is prorated to line up with it — you'll see the exact price next.
              </p>
            </div>
            <span className="inline-flex items-center gap-1 rounded-full bg-mint px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-ink">
              <ShieldCheck className="h-3 w-3" /> Razorpay
            </span>
          </div>
          <ul className="mt-4 space-y-2 text-sm text-ink/70">
            {["View & reply to every DM conversation", "Confirm orders, log returns, and manage customers"].map((f) => (
              <li key={f} className="flex items-center gap-2">
                <Check className="h-4 w-4 text-hotpink" /> {f}
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs text-ink/50">
            Members get full day-to-day fulfilment access, but billing, automations, and store settings stay owner-only. Payments aren't refunded, but the seat stays active for as long as you're on Pro.
          </p>
        </div>

        <div className="mt-6 flex flex-col gap-2 md:flex-row">
          <button
            onClick={onCancel}
            disabled={busy}
            className="rounded-full border border-ink/15 bg-white px-5 py-2.5 text-sm font-semibold text-ink hover:bg-ink/5 disabled:opacity-70"
          >
            Not now
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            data-testid="upgrade-seat-pay"
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-hotpink via-red-500 to-orange-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:opacity-95 disabled:opacity-70"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue <ArrowUpRight className="h-4 w-4" /></>}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

// -------------------- Confirm seat purchase modal ----------------------------
// Shown for EVERY seat purchase (first seat and any subsequent one) — always
// tells the merchant the exact price and the exact expiry date before any
// charge happens.
function ConfirmSeatDialog({ order, symbol, onCancel, onConfirm, busy }) {
  const amountLabel = formatSeatAmount(order.amount, symbol);
  const expiryLabel = formatSeatExpiry(order.expires_at);

  return (
    <ModalShell onClose={onCancel} testId="confirm-seat-dialog">
      <div className="p-8">
        <div className="grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-hotpink via-red-500 to-orange-500 text-white">
          <Calendar className="h-6 w-6" />
        </div>
        <h3 className="mt-5 text-2xl font-black tracking-tight text-ink">
          Confirm your seat
        </h3>
        <p className="mt-2 text-sm text-ink/60">
          {order.is_prorated
            ? "This seat is prorated to expire on the same date as your existing extra seat(s)."
            : "This starts a fresh 30-day period for your extra seat."}
        </p>

        <div className="mt-6 rounded-2xl border border-ink/10 bg-ink/[0.02] p-5">
          <div className="flex items-baseline justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-ink/50">
                {order.is_prorated ? "Prorated price" : "Price"}
              </p>
              <p className="mt-1 text-2xl font-black text-ink">{amountLabel}</p>
            </div>
            {order.is_prorated && (
              <span className="inline-flex items-center gap-1 rounded-full bg-lilac px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-ink">
                <Percent className="h-3 w-3" /> {order.remaining_days} day{order.remaining_days === 1 ? "" : "s"}
              </span>
            )}
          </div>

          <div className="mt-4 flex items-center gap-2 border-t border-ink/10 pt-4">
            <Calendar className="h-4 w-4 text-hotpink" />
            <p className="text-sm text-ink/70">
              Access until <span className="font-semibold text-ink">{expiryLabel}</span>
            </p>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-2 md:flex-row">
          <button
            onClick={onCancel}
            disabled={busy}
            className="rounded-full border border-ink/15 bg-white px-5 py-2.5 text-sm font-semibold text-ink hover:bg-ink/5 disabled:opacity-70"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            data-testid="confirm-seat-pay"
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-hotpink via-red-500 to-orange-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:opacity-95 disabled:opacity-70"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Confirm & pay {amountLabel} <ArrowUpRight className="h-4 w-4" /></>}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

// -------------------- Pro gate ---------------------------------------------
function ProGate({ navigate }) {
  return (
    <section className="min-h-screen bg-cream px-6 py-16">
      <div className="mx-auto max-w-lg">
        <BackToDashboard />
      </div>
      <div className="grid place-items-center px-0 pb-4 pt-8 text-center">
        <div className="max-w-lg text-center">
          <div className="mx-auto grid h-16 w-16 place-items-center rounded-3xl bg-lilac text-ink">
            <Lock className="h-7 w-7" />
          </div>
          <h1 className="mt-6 text-4xl font-black tracking-tight text-ink">Team is a Pro feature.</h1>
          <p className="mt-3 text-ink/60">
            Invite up to 2 teammates on Pro, and add more seats as you grow. Upgrade to unlock shared
            fulfilment.
          </p>
          <button
            data-testid="team-upgrade-cta"
            onClick={() => navigate("/plans")}
            className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-hotpink via-red-500 to-orange-500 px-6 py-3 text-sm font-semibold text-white shadow-sm"
          >
            Upgrade to Pro <ArrowUpRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
}

// -------------------- Shell + helpers --------------------------------------
function ModalShell({ children, onClose, testId }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid={testId}
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/60 p-0 md:items-center md:p-6"
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ type: "spring", damping: 24, stiffness: 260 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md overflow-hidden rounded-t-3xl bg-cream md:rounded-3xl"
      >
        {children}
      </motion.div>
    </motion.div>
  );
}

function ModalField({ label, required, error, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 flex items-baseline justify-between text-xs font-semibold uppercase tracking-wider text-ink/70">
        <span>
          {label}
          {required && <span className="ml-1 text-hotpink">*</span>}
        </span>
      </span>
      {children}
      {error && <span className="mt-1 block text-xs font-semibold text-hotpink">{error}</span>}
    </label>
  );
}
